export interface ToolCall {
  id: string
  name: string
  arguments: string | Record<string, unknown>
  reasoning?: string
  seq?: number
  result?: string
  parentMessageId?: string
  startTime?: number // Timestamp when tool call started
  endTime?: number // Timestamp when tool call completed
  timeElapsed?: number // Time elapsed in milliseconds
  isFrontend?: boolean // true when tool is in the frontend registry
  ended?: boolean
}

export interface Reasoning {
  id: string
  seq: number
  summary: string[]
}

export interface ChatRawMessageItem {
  id: string | null
  agentId: string | null
  parentId: string | null
  childrenIds: string[]
  role: 'user' | 'assistant'
  content: string
  resources: any[] // Array of resources related to the message
  images?: any[] // Optional images related to the message
  citations: any[] // Optional citation for the content
  aborted?: boolean // True when the persisted response was forcefully stopped mid-generation
  suggestedPrompts: any[] // Optional suggested prompts for the user
  modelVersion: string
  createdAt: string // Use `Date` if it's a Date object
  createdBy: string | null
  type: 'text' | 'loader' // Add other types if applicable
  runId: string | null // Optional run ID for tracking the message
  threadMcpServers: any[]
  // This message's Personal Databox scope - a sibling to threadMcpServers, not nested inside it
  personalDataBoxes?: Array<{ personalDataBoxId: string; resourceIds: string[] }>
  toolCalls?: ToolCall[] // Tool calls made during the message
  reasonings?: Reasoning[] // Ordered reasoning segments from AG-UI responses
  elapsedTime?: number // Time taken for this message in seconds
  reasoningContent?: string // Streamed reasoning/thinking text
}

export interface ChatBubbleResponse {
  id: string | null
  agentId: string | null
  content: string
  citations: any[] // source of content or data
  suggestedPrompts: any[]
  modelVersion: string
  runId: string | null // Optional run ID for tracking the response
  toolCalls?: ToolCall[] // Tool calls made during the response
  reasonings?: Reasoning[] // Ordered reasoning segments from AG-UI responses
  elapsedTime?: number // Time taken for this response in seconds
  reasoningContent?: string // Streamed reasoning/thinking text
  aborted?: boolean // True when the persisted response was forcefully stopped mid-generation
}

// Background ingestion state reported by the upload endpoint (Spec 012/013).
export type ResourceIngestionStatus =
  | 'NotApplicable'
  | 'Pending'
  | 'Processing'
  | 'Ready'
  | 'Failed'

export interface UploadResourceResponse {
  documentId: string
  embeddingTokenCount: number
  message: string
  status: string // "Success" | "Failed"
  ingestionStatus?: ResourceIngestionStatus | null
  reusedExisting?: boolean
}

// Lifecycle of a composer attachment chip. `attached` means raw storage
// succeeded; ingestion may still be running in the background.
export type PortalAttachmentState = 'uploading' | 'attached' | 'failed'

export interface FileProps {
  id?: string
  name: string
  content?: File | string | null
  type?: 'image' | 'file'
  state?: PortalAttachmentState
  documentId?: string
  ingestionStatus?: ResourceIngestionStatus | null
  reusedExisting?: boolean
  errorMessage?: string
}

export interface WebSearchToolConfig {
  mcpServerId: string
  toolName: string
}

// Generic MCP tool configuration - reusable for all MCP servers
export type McpToolConfig = {
  mcpServerId: string
  toolName: string
}

export interface ChatSubmitProps {
  prompt: string
  parentId: string | null
  isEditPrompt?: boolean
  messageId?: string
  files?: FileProps[]
  images?: any
  threadMcpServers?: any[] | null
  selectedDataboxes?: Array<{
    id: string
    name: string
    smartDocsDataboxId?: string
    teamIdentifier?: string
  }>
  selectedDataProducts?: Array<{ id: string; name: string; teamIdentifier?: string }>
  teamIdentifier?: string
  // Scalable array of tool configurations for all MCP servers
  mcpToolConfigs?: McpToolConfig[]
  // This thread's Personal Databox scope - a sibling to threadMcpServers, not nested inside it
  personalDataBoxes?: Array<{ personalDataBoxId: string; resourceIds: string[] }> | null
}

export interface ChatBubbleItem {
  parentId: null | string
  //childrenIds: string[]
  activeResponseIndex: number
  response: ChatBubbleResponse[]
  resources: any[]
  role: 'assistant' | 'user' // Add other roles if applicable
  type: 'text' | 'loader' // Add other types if applicable
}

export interface AgentData {
  id: number
  name: string
  avatarUrl?: string
  title: string
  description: string
  source: string
  colorCode: string
  skills: string[]
  metadata: any
  plugins: {
    name: string
    logo: string
    width: number
    height: number
  }[]
  agentType: 'Team' | 'Enterprise' | 'Personal'
  isPersonalAssistant?: boolean
  availabilityLabel?: string
  arc?: {
    isInProcess?: boolean
    arcRoles?: string[]
    hideArcRoles?: boolean
  }
  arcRoles?: Array<{
    id?: number
    role?: string
    description?: string
    group?: string
  }>
}

export const chatResponseSchema: ChatBubbleResponse = {
  id: null,
  agentId: null,
  content: '',
  citations: [],
  modelVersion: '',
  runId: null,
  suggestedPrompts: [],
  toolCalls: [],
  reasonings: [],
  elapsedTime: 0,
  reasoningContent: '',
  aborted: false
}

export const chatMessageSchema: ChatBubbleItem = {
  parentId: null,
  //childrenIds: [],
  activeResponseIndex: 0,
  response: [chatResponseSchema],
  resources: [],
  role: 'assistant',
  type: 'text'
}

export const chatRawMessageSchema: ChatRawMessageItem = {
  id: null,
  agentId: null,
  parentId: null,
  childrenIds: [],
  role: 'assistant',
  content: '',
  resources: [],
  citations: [],
  suggestedPrompts: [],
  modelVersion: '',
  createdAt: '',
  type: 'text',
  createdBy: '',
  runId: null,
  threadMcpServers: [],
  toolCalls: [],
  elapsedTime: 0,
  reasoningContent: ''
}
